import React, {useEffect, useState} from 'react';
import {MaterialCommunityIcons} from '@expo/vector-icons';

interface AssignmentRequiredPageProps {
  assignmentLink?: string;
  redirectSeconds?: number;
}

export function AssignmentRequiredPage({
  assignmentLink = '/Dashboard',
  redirectSeconds = 5,
}: AssignmentRequiredPageProps) {
  const [countdown, setCountdown] = useState(redirectSeconds);

  useEffect(() => {
    // Countdown timer
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Redirect after countdown
    const redirectTimer = setTimeout(() => {
      window.location.href = assignmentLink;
    }, redirectSeconds * 1000);

    return () => {
      clearInterval(timer);
      clearTimeout(redirectTimer);
    };
  }, [assignmentLink, redirectSeconds]);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Icon Section */}
        <div style={styles.iconSection}>
          <div style={styles.iconBadge}>
            <MaterialCommunityIcons
              name="clipboard-list-outline"
              size={56}
              color="#4D3F37"
            />
          </div>
        </div>

        {/* Heading */}
        <h1 style={styles.heading}>Level Assessment Required</h1>

        {/* Message */}
        <p style={styles.message}>
          We need to understand your current chess level to suggest the perfect
          plan for you.
        </p>

        {/* Instruction */}
        <div style={styles.instructionBox}>
          <p style={styles.instructionText}>
            Please complete your assignment to help us assess your level. Once
            completed, we'll be able to recommend the best learning plan
            tailored to your needs.
          </p>
        </div>

        {/* Countdown Timer */}
        <div style={styles.countdownSection}>
          <p style={styles.countdownText}>
            Redirecting in{' '}
            <span style={styles.countdownNumber}>{countdown}</span>{' '}
            {countdown === 1 ? 'second' : 'seconds'}...
          </p>
        </div>

        {/* Manual Redirect Button */}
        <button
          style={styles.redirectButton}
          onClick={() => {
            window.location.href = assignmentLink;
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#D4A017';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#FACF47';
          }}>
          <MaterialCommunityIcons
            name="arrow-right"
            size={20}
            color="#4D3F37"
          />
          <span style={styles.buttonText}>Go to Assignment Now</span>
        </button>

        {/* Additional Info */}
        <div style={styles.infoBox}>
          <MaterialCommunityIcons
            name="information-outline"
            size={20}
            color="#8A7968"
          />
          <p style={styles.infoText}>
            The assignment typically takes 15-20 minutes to complete. Your
            results will help us personalize your learning journey.
          </p>
        </div>
      </div>
    </div>
  );
}

// Design System Colors (matching SuccessPage)
const colors = {
  background: '#FAF8F5',
  primaryCTA: '#FACF47',
  primaryCTAHover: '#D4A017',
  textPrimary: '#4D3F37',
  textSecondary: '#8A7968',
  border: '#E8E3DC',
  white: '#FFFFFF',
};

const styles: {[key: string]: React.CSSProperties} = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily:
      'Inter, Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  content: {
    width: '100%',
    maxWidth: '672px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
  },
  iconSection: {
    marginTop: '40px',
    marginBottom: '8px',
  },
  iconBadge: {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    backgroundColor: colors.primaryCTA,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  heading: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: '0 0 16px 0',
    lineHeight: '1.2',
    textAlign: 'center',
  },
  message: {
    fontSize: '18px',
    color: colors.textSecondary,
    margin: 0,
    lineHeight: '1.5',
    textAlign: 'center',
    maxWidth: '600px',
  },
  instructionBox: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `2px solid ${colors.primaryCTA}`,
    padding: '24px',
    width: '100%',
    marginTop: '8px',
  },
  instructionText: {
    fontSize: '16px',
    color: colors.textPrimary,
    lineHeight: '1.6',
    textAlign: 'center',
  },
  countdownSection: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    padding: '16px 24px',
    border: `1px solid ${colors.border}`,
    marginTop: '8px',
  },
  countdownText: {
    fontSize: '16px',
    color: colors.textSecondary,
    textAlign: 'center',
  },
  countdownNumber: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.textPrimary,
  },
  redirectButton: {
    backgroundColor: colors.primaryCTA,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 0.2s ease',
    marginTop: '8px',
  },
  buttonText: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.textPrimary,
  },
  infoBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.border}`,
    marginTop: '16px',
    maxWidth: '600px',
  },
  infoText: {
    fontSize: '14px',
    color: colors.textSecondary,
    lineHeight: '1.5',
    flex: 1,
  },
};

export default AssignmentRequiredPage;
